import { Box, Button, Grid, makeStyles, Dialog, DialogTitle, DialogContent, IconButton } from "@material-ui/core"
import { GeoLocationIcon, CloseIcon, LiveLocationIcon, DriversIcon, AidMonitorsIcon, CancelWhiteIcon } from "../../libs/icons"
import NCText from "../../shared/NCText"
import NCInput from "../../shared/NCInput"
import NCLoader from "../../shared/NCLoader"
import NCTable from "../../shared/table/NCTable"
import NCModal from "../../shared/NCModal"
import NCButton from "../../shared/NCButton"
import { useEffect, useMemo, useRef, useState } from "react"
import { TABLE_CONFIG } from "../../libs/config"
import API from "../../libs/api"
import { API_PATH } from "../../libs/config"
import { toast } from "react-toastify"
import { QRCodeCanvas } from "qrcode.react"
import { useGetRideGroups, useGetTripStops } from "../../services/queries"
import { formatAMPM, formatDate } from "../../libs/utils"
import { STOPS_CALL_INTERVAL, STOP_STATUS, STOP_TYPE, formatRideGroupStatus } from "../../libs/const"
import MapComponent from "../../components/map/Map"
import AddChangeAidMonitorModal from "../driver/AddChangeAidMonitorModal"
import RescueDriverModal from "./RescueDriverModal"

const useStyles = makeStyles(() => ({
  headingContainer: {
    backgroundColor: "#F9FAFB"
  },
  boxContainer: {
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    overflow: "hidden"
  }
}))

const ScheduledTrip = ({ startTime, endTime, driver, search }) => {
  const cls = useStyles()

  const [stops, setStops] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(null)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(TABLE_CONFIG.PAGE_SIZE)

  // Reset to page 1 during render (before the query fires) when any filter
  // changes, otherwise a stale page number hits DRF's "Invalid page" 404.
  const [prevFilters, setPrevFilters] = useState({ search, driver, startTime, endTime })
  if (
    search !== prevFilters.search ||
    driver !== prevFilters.driver ||
    startTime !== prevFilters.startTime ||
    endTime !== prevFilters.endTime
  ) {
    setPrevFilters({ search, driver, startTime, endTime })
    setPage(1)
  }

  const [reassignModal, setReassignModal] = useState(false)
  const [reassignTripId, setReassignTripId] = useState(null)
  const [reassignGroupId, setReassignGroupId] = useState(null)

  const [rescueModalGroupId, setRescueModalGroupId] = useState(null)

  const [cancelConfirmGroupId, setCancelConfirmGroupId] = useState(null)
  const [cancelReason, setCancelReason] = useState("")

  const [trackingModalGroup, setTrackingModalGroup] = useState(null)

  const [selectedMapStop, setSelectedMapStop] = useState(null)

  const stopsInterval = useRef(null)

  const { isLoading, data, refetch: groupsRefetch } = useGetRideGroups(
    {
      page,
      search: search || undefined,
      page_size: pageSize,
      driver: driver || undefined,
      ...(startTime && endTime ? {
        service_date_after: `${startTime.getFullYear()}-${String(startTime.getMonth() + 1).padStart(2, "0")}-${String(startTime.getDate()).padStart(2, "0")}`,
        service_date_before: `${endTime.getFullYear()}-${String(endTime.getMonth() + 1).padStart(2, "0")}-${String(endTime.getDate()).padStart(2, "0")}`,
      } : {}),
    },
    { keepPreviousData: false }
  )

  const {
    data: stopsResp,
    refetch: stopsRefetch
  } = useGetTripStops(
    { page_size: 100, ride_group: selectedGroup },
    { enabled: !!selectedGroup }
  )

  const { next, previous, results: groupData } = data?.data?.data || {}
  const { results: stopsData } = stopsResp?.data?.data || {}

  // Deduplicate: remove duplicate IDs within each group's string,
  // and collapse only TRUE duplicates (same group id). Distinct groups for the
  // same request (e.g. split across time windows — 06:00 vs 08:00) must all
  // show; deduping by request ID wrongly hid every group after the first.
  const uniqueGroupData = useMemo(() => {
    if (!groupData) return []
    const seen = new Set()
    return groupData
      .map(g => {
        const ids = (g.ride_request_ids_str || "").split(",").map(s => s.trim()).filter(Boolean)
        const unique = [...new Set(ids)]
        return { ...g, ride_request_ids_str: unique.join(", ") }
      })
      .filter(g => {
        const key = g.id
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
  }, [groupData])

  const onNext = () => setPage(prev => (next ? prev + 1 : prev))
  const onPrev = () => setPage(prev => (previous ? prev - 1 : prev))

  const selectedGroupData = uniqueGroupData?.find(g => g.id === selectedGroup)
  const selectedGroupLabel = selectedGroupData?.ride_request_ids_str || `Group #${selectedGroup}`

  const rescueGroupData = uniqueGroupData?.find(g => g.id === rescueModalGroupId)

  const onRowClicked = row => {
    const group = uniqueGroupData[row.index]
    setSelectedGroup(prev => prev === group?.id ? null : group?.id)
  }

  const cancelNoShow = async (groupId) => {
    try {
      await API.post(API_PATH.CANCEL_GROUP(groupId), { reason: cancelReason })
      toast.success("Ride cancelled.")
      setCancelReason("")
      groupsRefetch()
      stopsRefetch()
    } catch (e) {
      toast.error("Failed to cancel ride")
    }
  }

  const groupColumns = [
    {
      Header: "Request ID",
      id: "request_id",
      accessor: cell => cell.ride_request_ids_str || cell.id,
      Cell: ({ value }) => <Box pt={1} pb={1}><NCText>{value}</NCText></Box>,
      disableSortBy: true
    },
    {
      Header: "Service Date",
      id: "service_date",
      accessor: cell => formatDate(cell.service_date),
      disableSortBy: true
    },
    {
      Header: "Driver",
      id: "driver",
      disableSortBy: true,
      accessor: cell => cell.driver?.name || "-"
    },
    {
      Header: "Aid/Monitor",
      id: "aid_monitor",
      disableSortBy: true,
      accessor: cell => cell.aid_monitor?.name || "-"
    },
    {
      Header: "Pax",
      id: "passengers",
      disableSortBy: true,
      accessor: cell => cell.passenger_count || cell.passengers?.length || 0
    },
    {
      Header: "Status",
      id: "status",
      disableSortBy: true,
      accessor: cell => formatRideGroupStatus(cell.status)
    },
    {
      Header: "Hold",
      id: "hold",
      disableSortBy: true,
      accessor: cell => cell,
      Cell: ({ value }) => {
        return (
          <Box pt={1} pb={1}>
            <Box display="flex" gridGap={4} flexWrap="wrap">
              <Button
                size="small"
                title="Rescue Driver"
                style={{ background: "#FF9800", color: "#fff", minWidth: 24, padding: "2px", borderRadius: 4 }}
                onClick={(e) => {
                  e.stopPropagation()
                  setRescueModalGroupId(value.id)
                }}
              >
                <DriversIcon style={{ width: 14, height: 14 }} />
              </Button>
              <Button
                size="small"
                title="Reassign A/M"
                style={{ background: "#4CAF50", color: "#fff", minWidth: 24, padding: "2px", borderRadius: 4 }}
                onClick={(e) => {
                  e.stopPropagation()
                  const trips = value.trips || value.passengers || []
                  setReassignTripId(trips[0]?.id || null)
                  setReassignGroupId(value.id || null)
                  setReassignModal(true)
                }}
              >
                <AidMonitorsIcon style={{ width: 14, height: 14 }} />
              </Button>
              <Button
                size="small"
                title="Cancel"
                style={{ background: "#E22134", color: "#fff", minWidth: 24, padding: "2px", borderRadius: 4 }}
                onClick={(e) => {
                  e.stopPropagation()
                  setCancelReason("")
                  setCancelConfirmGroupId(value.id)
                }}
              >
                <CancelWhiteIcon style={{ width: 14, height: 14 }} />
              </Button>
            </Box>
          </Box>
        )
      }
    },
    {
      Header: "",
      id: "tracking",
      disableSortBy: true,
      width: 50,
      Cell: ({ row }) => {
        const group = row.original
        if (!group?.tracking_uuid) return null
        return (
          <IconButton
            size="small"
            title="Share tracking link"
            onClick={(e) => {
              e.stopPropagation()
              setTrackingModalGroup(group)
            }}
          >
            <LiveLocationIcon style={{ color: "#1a73e8", width: 20, height: 20 }} />
          </IconButton>
        )
      }
    }
  ]

  const stopColumns = [
    { Header: "Seq", id: "seq", accessor: cell => cell.seq, disableSortBy: true },
    { Header: "Client / A/M", id: "client", disableSortBy: true, accessor: cell => cell.client_name },
    { Header: "Address", id: "address", accessor: cell => cell.address, disableSortBy: true },
    { Header: "Scheduled", id: "arrival", disableSortBy: true, accessor: cell => cell.arrival_display || "-" },
    { Header: "ETA", id: "eta", disableSortBy: true, accessor: cell => {
      if (cell.statusRaw === 3 && cell.actual_arrival) return formatAMPM(cell.actual_arrival).toUpperCase()
      return cell.est_arrival ? formatAMPM(cell.est_arrival).toUpperCase() : "-"
    }},
    { Header: "Type", id: "type", disableSortBy: true, accessor: cell => cell.type },
    { Header: "Status", id: "status", disableSortBy: true, accessor: cell => formatRideGroupStatus(cell.status) },
    {
      Header: "",
      id: "map",
      disableSortBy: true,
      width: 50,
      Cell: ({ row }) => {
        const stop = row.original
        if (!stop?.latitude || !stop?.longitude) return null
        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedMapStop(stop)
            }}
          >
            <GeoLocationIcon />
          </IconButton>
        )
      }
    },
  ]

  useEffect(() => {
    // clear stops when switching groups to avoid showing stale data
    setStops([])
  }, [selectedGroup])

  useEffect(() => {
    // Hide the selected group's Stops panel when pagination changes,
    // since the selected group is no longer in view.
    setSelectedGroup(null)
    setStops([])
  }, [page, pageSize])

  useEffect(() => {
    if (stopsData) {
      // Determine the baseline date from the first stop's scheduled_time
      const firstArrival = stopsData[0]?.scheduled_time
      const firstDate = firstArrival ? new Date(firstArrival).toDateString() : null
      const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]

      const s = stopsData.map(stop => {
        const rawTime = stop?.scheduled_time
        let scheduledDisplay = rawTime ? formatAMPM(rawTime).toUpperCase() : "-"
        // If this stop is on a different date than the first stop, prepend date
        if (rawTime && firstDate) {
          const stopDate = new Date(rawTime)
          if (!isNaN(stopDate) && stopDate.toDateString() !== firstDate) {
            scheduledDisplay = `${monthNames[stopDate.getMonth()]} ${stopDate.getDate()} ${scheduledDisplay}`
          }
        }
        return {
          seq: stop.id,
          client_name: stop?.client_name || "-",
          address: stop?.address?.address || "-",
          est_arrival: stop?.eta,
          actual_arrival: stop?.actual_arrived_time,
          arrival: stop?.scheduled_time,
          arrival_display: scheduledDisplay,
          type: STOP_TYPE[stop?.type] || stop?.type,
          status: STOP_STATUS[stop?.status] || stop?.status,
          statusRaw: stop?.status,
          latitude: stop?.address?.latitude != null ? Number(stop.address.latitude) : null,
          longitude: stop?.address?.longitude != null ? Number(stop.address.longitude) : null,
        }
      }) || []
      setStops(s)
    }
  }, [stopsData])

  useEffect(() => {
    if (selectedGroup) {
      stopsInterval.current = setInterval(() => stopsRefetch(), STOPS_CALL_INTERVAL)
    }
    return () => clearInterval(stopsInterval.current)
  }, [selectedGroup])

  return (
    <Grid container>
      <Grid item xs={12} style={{ marginTop: 16 }}>
        <Box>
          <Box className={cls.boxContainer} mb={3}>
            <Box className={cls.headingContainer} px={2} py={1} borderRadius="4px 4px 0 0">
              <NCText style={{ fontWeight: 600, fontSize: 16 }}>Ride Groups</NCText>
            </Box>
            <Box px={2} pb={2}>
              <NCTable
                rowHighlight
                inlineFooter
                columns={groupColumns}
              data={uniqueGroupData || []}
              onRowClicked={onRowClicked}
              loading={isLoading}
              onNext={onNext}
              onPrev={onPrev}
              gotoPage={setPage}
              pageSize={pageSize}
              page={page}
              totalPages={data?.data?.data?.total_pages || 1}
              pageCount={data?.data?.data?.total_pages || 1}
              count={data?.data?.data?.count || 0}
              setPageSize={setPageSize}
            />
              {isLoading && <NCLoader open={true} />}
            </Box>
          </Box>

          {selectedGroup && stops.length > 0 && (
            <Box className={cls.boxContainer} mb={3}>
              <Box className={cls.headingContainer} px={2} py={1} borderRadius="4px 4px 0 0" display="flex" justifyContent="space-between" alignItems="center">
                <NCText style={{ fontWeight: 600, fontSize: 16 }}>Stops — {selectedGroupLabel}</NCText>
                <NCText style={{ fontWeight: 600, fontSize: 14, color: "#6B7280" }}>{stops.length} stops</NCText>
              </Box>
              <Box px={2} pb={2}>
                <NCTable
                columns={stopColumns}
                data={stops}
                pageSize={stops.length}
                hidePagination
                inlineFooter
              />
              </Box>
            </Box>
          )}
        </Box>
      </Grid>

      <AddChangeAidMonitorModal
        modalState={reassignModal}
        tripId={reassignTripId}
        groupId={reassignGroupId}
        handleClose={() => {
          setReassignModal(false)
          setReassignGroupId(null)
          groupsRefetch()
          stopsRefetch()
        }}
      />

      <NCModal
        open={!!cancelConfirmGroupId}
        title="Cancel Ride"
        onClose={() => { setCancelConfirmGroupId(null); setCancelReason("") }}
        submitButton={
          <NCButton
            variant="contained"
            color="secondary"
            disableElevation
            disabled={!cancelReason.trim()}
            onClick={() => {
              cancelNoShow(cancelConfirmGroupId)
              setCancelConfirmGroupId(null)
            }}
            style={{ width: "auto" }}
          >
            Yes, Cancel
          </NCButton>
        }
      >
        <Box mt={1} width="100%">
          <NCText style={{ fontSize: 14, display: "block", marginBottom: 20 }}>
            You are about to cancel a ride. This action cannot be undone. Are you sure you want to proceed?
          </NCText>
          <NCText style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, display: "block" }}>Why cancel this ride?</NCText>
          <NCInput
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            placeholder="Enter reason for cancellation..."
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
          />
        </Box>
      </NCModal>

      <RescueDriverModal
        modalState={!!rescueModalGroupId}
        groupId={rescueModalGroupId}
        currentDriverId={rescueGroupData?.driver?.id}
        currentAidMonitorId={rescueGroupData?.aid_monitor?.id}
        handleClose={() => {
          setRescueModalGroupId(null)
          groupsRefetch()
          stopsRefetch()
        }}
      />

      <Dialog open={!!selectedMapStop} onClose={() => setSelectedMapStop(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <NCText>Stop Map — {selectedMapStop?.client_name || "Stop"} (#{selectedMapStop?.seq})</NCText>
            <IconButton size="small" onClick={() => setSelectedMapStop(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ height: 500, padding: 0 }}>
          {selectedMapStop && (
            <MapComponent
              center={{ lat: selectedMapStop.latitude, lng: selectedMapStop.longitude }}
              stops={[{
                seq: selectedMapStop.seq,
                latitude: selectedMapStop.latitude,
                longitude: selectedMapStop.longitude,
                blueMark: true,
              }]}
              zoom={15}
              zoomControl
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Tracking link modal with QR code */}
      <Dialog
        open={!!trackingModalGroup}
        onClose={() => setTrackingModalGroup(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <NCText style={{ fontWeight: 700, fontSize: 17 }}>Share Tracking Link</NCText>
            <IconButton size="small" onClick={() => setTrackingModalGroup(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gridGap={16}>
            {/* QR Code */}
            <Box p={1.5} style={{ border: "1px solid #E5E7EB", borderRadius: 12 }}>
              {trackingModalGroup?.tracking_uuid && (
                <QRCodeCanvas
                  value={`${window.location.origin}/track/${trackingModalGroup.tracking_uuid}`}
                  size={180}
                />
              )}
            </Box>

            {/* URL display */}
            <Box
              p={1.5}
              style={{ background: "#F3F4F6", borderRadius: 10, width: "100%", textAlign: "center" }}
            >
              <NCText
                style={{ fontSize: 13, color: "#1a73e8", textDecoration: "underline", wordBreak: "break-all" }}
                onClick={() => window.open(`/track/${trackingModalGroup?.tracking_uuid}`, "_blank")}
              >
                {`${window.location.origin}/track/${trackingModalGroup?.tracking_uuid}`}
              </NCText>
            </Box>

            {/* Buttons */}
            <Box display="flex" gridGap={12} width="100%" justifyContent="center">
              <NCButton
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/track/${trackingModalGroup?.tracking_uuid}`
                  )
                  toast.success("Link copied to clipboard")
                }}
                style={{ flex: 1, maxWidth: 140 }}
              >
                Copy Link
              </NCButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Grid>
  )
}

export default ScheduledTrip
